import styles from './AdditionalMealExpensesCardView.module.scss';

import { Typography } from '@mui/material';
import { DateTime } from 'luxon';
import React, { useCallback, useContext, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { NavigationOverlay } from '@work4all/components/lib/navigation/navigation-overlay';

import { TravelAdditionalMealExpenses } from '@work4all/models/lib/Classes/TravelAdditionalMealExpenses.entity';

import { formatCurrency, formatNumberAsCurrency } from '@work4all/utils';
import { DateTimeCustom } from '@work4all/utils/lib/date-utils/formatDateString';

import { CurrencyExchangeInfoContext } from '../../inbound-invoice/currency-exchange-info-context';

import {
  AdditionalMealExpenseEditOverlay,
  AdditionalMealExpenseEditOverlayProps,
} from './AdditionalMealExpenseEditOverlay';

interface AdditionalMealExpensesCardViewProps
  extends Pick<AdditionalMealExpenseEditOverlayProps, 'update' | 'onVerify'> {
  tableData: TravelAdditionalMealExpenses[];
}

export const AdditionalMealExpensesCardView: React.FC<
  AdditionalMealExpensesCardViewProps
> = ({ onVerify, tableData, update }) => {
  const { t } = useTranslation();
  const exchangeInfoContext = useContext(CurrencyExchangeInfoContext);

  const [expenseId, setExpenseId] = useState<number | null>(null);

  const handleEditExpense = useCallback(
    (expense: TravelAdditionalMealExpenses) => {
      setExpenseId(expense.id);
    },
    []
  );

  const expense = useMemo(() => {
    return tableData.find((expense) => expense.id === expenseId);
  }, [tableData, expenseId]);

  if (!tableData || tableData.length === 0) {
    return (
      <div className={styles.noData}>
        <Typography variant="caption" color="textSecondary">
          {t('COMMON.NO_DATA')}
        </Typography>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <NavigationOverlay
        key={expenseId}
        open={expenseId !== null}
        close={() => setExpenseId(null)}
        initialView={{
          view: (
            <AdditionalMealExpenseEditOverlay
              expense={expense}
              onVerify={onVerify}
              update={update}
            />
          ),
        }}
      />
      {tableData.map((expense, index) => (
        <div
          key={expense.id || index}
          onClick={() => handleEditExpense(expense)}
          className={styles.expenseItem}
        >
          <div className={styles.header}>
            <Typography variant="body1" fontWeight={600}>
              {expense.dateTime
                ? DateTime.fromISO(expense.dateTime).toLocaleString(
                    DateTimeCustom.DATE_SIMPLE_2YEAR
                  )
                : ''}
            </Typography>
            <Typography variant="caption" color="textSecondary">
              {expense.hours}h
            </Typography>
          </div>

          <div className={styles.row}>
            <Typography variant="caption" color="textSecondary" gutterBottom>
              {t('TRAVEL.AMOUNT')}
            </Typography>
            <Typography variant="body1" fontWeight={600}>
              {formatNumberAsCurrency(expense.amount || 0)}
              {exchangeInfoContext?.defaultCurrency
                ? ` ${formatCurrency(exchangeInfoContext.defaultCurrency, t)}`
                : ''}
            </Typography>
          </div>

          {(expense.deductionBreakfast ||
            expense.deductionLunch ||
            expense.deductionDinner) && (
            <div className={styles.row}>
              <Typography variant="caption" color="textSecondary">
                {t('TRAVEL.DEDUCTIONS')}
              </Typography>
              <div className={styles.deductions}>
                <Typography
                  variant="caption"
                  color="error"
                  className={styles.deductionAmount}
                >
                  {formatNumberAsCurrency(
                    expense.deductionBreakfastAmount || 0
                  )}
                </Typography>
                <Typography
                  variant="caption"
                  color="error"
                  className={styles.deductionAmount}
                >
                  |
                </Typography>
                <Typography
                  variant="caption"
                  color="error"
                  className={styles.deductionAmount}
                >
                  {formatNumberAsCurrency(expense.deductionLunchAmount || 0)}
                </Typography>
                <Typography
                  variant="caption"
                  color="error"
                  className={styles.deductionAmount}
                >
                  |
                </Typography>
                <Typography
                  variant="caption"
                  color="error"
                  className={styles.deductionAmount}
                >
                  {formatNumberAsCurrency(expense.deductionDinnerAmount || 0)}
                </Typography>
              </div>
            </div>
          )}

          {expense.project && (
            <div className={styles.row}>
              <Typography variant="caption" color="textSecondary" gutterBottom>
                {t('COMMON.PROJECT')}
              </Typography>
              <Typography variant="caption">{expense.project.name}</Typography>
            </div>
          )}
          {expense.company && (
            <div className={styles.row}>
              <Typography variant="caption" color="textSecondary" gutterBottom>
                {t('TRAVEL.CUSTOMER_SUPPLIER')}
              </Typography>
              <Typography variant="caption">
                {expense.company?.data?.name}
              </Typography>
            </div>
          )}

          {expense.note && (
            <div className={styles.row}>
              <Typography variant="caption" color="textSecondary" gutterBottom>
                {t('INPUTS.NOTE/REMARK')}
              </Typography>
              <Typography variant="caption">{expense.note}</Typography>
            </div>
          )}
        </div>
      ))}
    </div>
  );
};
